// Operations › Profit, Blueprint only (pages/ux-full screens/ads.js: #/home/profit, #/home/profit/jobs). ui-web's
// views (ProfitViews.tsx), each from its own action's answer: what yesterday's jobs made after their
// materials, purchases, receipts and paid labour, against the target margin (reports.jobCosting), beside the month against the goal (gp.pace) and
// the huddle's last days (gp.huddle); and a day job by job and by technician, from each job's own record
// (jobs.get). Whoever holds finance.margins.view (profit-model.ts).
import { useEffect, useState } from 'react';
import { dateText } from '@flowology/kernel';
import {
  CostedJob,
  HuddleDays,
  HuddleDaysProps,
  JobMargins,
  JobMarginsProps,
  ProfitByTech,
  ProfitDay,
  ProfitDayProps,
  Section,
  viewFor,
  type OpenTarget,
} from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { addDays, todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { Drawn } from './Ads.tsx';
import { officeHost } from './host.ts';
import './home.css';

const tzOf = (nav: NavProps) => nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
const dayWords = (date: string, tz: string) => dateText(date, tz, { weekday: 'long', year: false });

// ── What did yesterday's work make? ──

export function ProfitScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const yesterday = addDays(todayIn(tz), -1);
  const c = useQuery<unknown>('reports.jobCosting', { from: yesterday, to: yesterday });
  const pace = useQuery<unknown>('gp.pace', {});
  const h = useQuery<unknown>('gp.huddle', {});
  const host = officeHost(onOpen);
  return (
    <div className="app bp-home bp-profit">
      <Header
        nav={nav}
        title="Gross profit"
        lede="What yesterday’s jobs made after their materials, purchases and paid labour, and where the month stands."
      >
        <button className="btn" onClick={() => nav.onView('profitjobs')}>
          By job and technician
        </button>
        <button className="btn" onClick={() => nav.onView('huddle')}>
          The month so far
        </button>
      </Header>
      <main className="flw flw-office bp-home-body">
        <div className="bp-grid2">
          <div>
            <Section title="Yesterday">
              <Drawn
                q={c}
                schema={ProfitDayProps}
                what="yesterday’s jobs"
                build={(r: object) => ({ ...r, label: `Yesterday, ${dayWords(yesterday, tz)}` })}
                draw={(p) => <ProfitDay data={p} host={host} />}
              />
            </Section>
          </div>
          <div>
            <Section title="The month against the goal">
              {pace.data == null ? (
                <Loading error={pace.error} what="the month" retry={() => void pace.reload()} />
              ) : (
                (viewFor('gp.pace', pace.data, host) ?? (
                  <p className="flw-empty" role="status">
                    Flowology can’t show the month here.
                  </p>
                ))
              )}
            </Section>
            <Section title="The morning huddle">
              <Drawn
                q={h}
                schema={HuddleDaysProps}
                what="the huddle"
                draw={(p) => <HuddleDays data={p} host={host} />}
              />
            </Section>
          </div>
        </div>
      </main>
    </div>
  );
}

// ── What did each job and technician make? ──

/** Each costed job's own record (jobs.get), for its customer and who was on it. */
function useJobs(ids: readonly string[]): { jobs: CostedJob[] | null; error: string | null } {
  const [jobs, setJobs] = useState<CostedJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = ids.join(',');
  useEffect(() => {
    let live = true;
    setJobs(null);
    void Promise.all(ids.map((id) => client.call<unknown>('jobs.get', { id })))
      .then((list) => live && setJobs(list.map((j) => CostedJob.parse(j))))
      .catch(() => live && setError('Couldn’t load who was on these jobs.'));
    return () => {
      live = false;
    };
    // the ids are compared by value (key), so a new list of the same jobs doesn't load them again
  }, [key]);
  return { jobs, error };
}

export function ProfitJobsScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const yesterday = addDays(todayIn(tz), -1);
  const [day, setDay] = useState(yesterday);
  const c = useQuery<{ rows?: { jobId: string }[] }>('reports.jobCosting', { from: day, to: day });
  // only the shown day's answer, never the day before's under its heading (QA-682): while the next day
  // loads, or once it's refused, the page waits or says why
  const costing = c.current ? c.data : null;
  const { jobs, error } = useJobs(costing?.rows?.map((r) => r.jobId) ?? []);
  const host = officeHost(onOpen);
  const words = dayWords(day, tz);
  const both = { ...c, data: costing && jobs ? costing : null, error: c.error ?? error };
  const props = (r: object) => ({ label: words, costing: r, jobs: jobs ?? [] });
  return (
    <div className="app bp-home bp-profit bp-ads">
      <Header
        nav={nav}
        title="By job and technician"
        lede="Each job’s gross profit on a day, and each technician’s for every hour they logged."
      >
        <div className="seg" role="group" aria-label="Day">
          <button className="btn quiet" onClick={() => setDay(addDays(day, -1))}>
            ‹ {dateText(addDays(day, -1), tz, { weekday: 'short', year: false })}
          </button>
          <button className="btn quiet" disabled={day === yesterday} onClick={() => setDay(yesterday)}>
            Yesterday
          </button>
          <button className="btn quiet" disabled={day >= yesterday} onClick={() => setDay(addDays(day, 1))}>
            {dateText(addDays(day, 1), tz, { weekday: 'short', year: false })} ›
          </button>
        </div>
      </Header>
      <main className="flw flw-office bp-home-body">
        <div className="bp-grid2">
          <div>
            <Section title={`Jobs, ${words}`} {...(costing?.rows ? { count: costing.rows.length } : {})}>
              <Drawn
                q={both}
                schema={JobMarginsProps}
                what="the jobs"
                build={props}
                draw={(p) => <JobMargins data={p} host={host} />}
              />
            </Section>
          </div>
          <div>
            <Section title="By technician">
              <Drawn
                q={both}
                schema={JobMarginsProps}
                what="who was on these jobs"
                build={props}
                draw={(p) => <ProfitByTech data={p} host={host} />}
              />
            </Section>
          </div>
        </div>
      </main>
    </div>
  );
}
